import React, { useState } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';

const INTERESTS = [
  'Videra Healthcare','Videra Hard Services','Videra Construction','Videra PM',
  'Videra Vanguard','Handback Readiness','International P3 / PPP',
  'SafeTrace','Firestopping Business Dashboard','Keystone Consultancy','Not sure yet',
];

const FORMSPREE_URL = 'https://formspree.io/f/xdkogozr';

const V2Contact = () => {
  useSEO({
    title: 'Contact Keystone | Start the conversation',
    description: 'Tell us what you\'re trying to improve. Keystone will help identify the most appropriate Videra route.',
    path: '/contact',
  });

  const [state, setState] = useState({ submitting: false, done: false, error: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ submitting: true, done: false, error: '' });
    const form = new FormData(e.currentTarget);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        body: form,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Submission failed. Please try again.');
      setState({ submitting: false, done: true, error: '' });
      toast.success('Thanks — we\'ll be in touch soon.');
      e.target.reset();
    } catch (err) {
      setState({ submitting: false, done: false, error: err.message });
      toast.error('Could not send', { description: err.message });
    }
  };

  return (
    <div data-testid="v2-contact">
      <ProductHero
        eyebrow="Contact"
        title="Tell us what you're trying to improve."
        body="You don't need to know which Videra product you need before talking to us. Tell us about the problem, process or delivery environment you're trying to improve and we'll help identify the most appropriate route."
        testid="contact-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {state.done ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-emerald-800 flex items-start gap-3" data-testid="contact-success">
              <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
              <div>
                <p className="text-lg font-medium">Thanks — we&rsquo;ve got it.</p>
                <p className="text-sm mt-1">A member of the Keystone team will reply personally within one working day.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Name" name="name" required testid="contact-name" />
                <Field label="Organisation" name="organisation" required testid="contact-org" />
                <Field label="Email" name="email" type="email" required testid="contact-email" />
                <Field label="Telephone" name="telephone" testid="contact-phone" />
                <Field label="Country" name="country" testid="contact-country" />
                <Field label="Sector" name="sector" testid="contact-sector" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">Interest</label>
                <select name="interest" required className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="contact-interest">
                  <option value="">Choose one…</option>
                  {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">Message</label>
                <textarea
                  name="message"
                  rows={5}
                  required
                  className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What problem are you trying to solve?"
                  data-testid="contact-message"
                />
              </div>
              {state.error && (
                <p className="text-sm text-red-600" data-testid="contact-error">{state.error}</p>
              )}
              <button
                type="submit"
                disabled={state.submitting}
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send className="w-4 h-4" /> {state.submitting ? 'Sending…' : 'Start the conversation'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = 'text', required, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1.5">
      {label}{!required && <span className="text-slate-400 ml-1 normal-case tracking-normal">— optional</span>}
    </label>
    <input
      name={name}
      type={type}
      required={required}
      className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      data-testid={testid}
    />
  </div>
);

export default V2Contact;
